import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Container,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  fetchAgentsStats,
  fetchTotalsStats,
  fetchCompanyStats,
} from '../../services/company-stats';
import {
  AgentsStatsResponse,
  TotalsStatsResponse,
  CompanyStatsResponse,
} from '../../services/company-stats/types';
import Iconify from '../../components/Iconify';
import Loader from '../../components/Loader';
import { UploadAssetsApiCall } from '../../utils/uploadAssetsApiCall';
import { getAssetURl } from '../../utils/url';
import { getItem } from '../../utils/localStorage';
import { updateUserProfile } from '../../store/users/api';
import { setUserProfile } from '../../store/auth/authSlice';
import { toast } from 'react-toastify';

// Utility function for consistent date formatting
const formatDateToYYYYMMDD = (date: Date | null): string => {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const CompanyDashboard = () => {
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector((state) => state.auth.user);
  const { name, id } = userProfile;
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const organizationId = getItem('oinfo')?.id || null;
  
  const [agentsStats, setAgentsStats] = useState<AgentsStatsResponse | null>(null);
  const [allAgentsStats, setAllAgentsStats] = useState<AgentsStatsResponse | null>(null);
  const [totalsStats, setTotalsStats] = useState<TotalsStatsResponse | null>(null);
  const [companyStats, setCompanyStats] = useState<CompanyStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    userProfile?.userProfileImage?.key 
      ? getAssetURl(userProfile.userProfileImage.key)
      : null
  );

  // Update preview when user profile changes
  useEffect(() => {
    if (userProfile?.userProfileImage?.key) {
      setProfileImagePreview(getAssetURl(userProfile.userProfileImage.key));
    }
  }, [userProfile?.userProfileImage?.key]);
  
  // Date range state
  const [startDate] = useState<Date | null>(null);
  const [endDate] = useState<Date | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Search state
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const startDateStr = startDate ? formatDateToYYYYMMDD(startDate) : undefined;
        const endDateStr = endDate ? formatDateToYYYYMMDD(endDate) : undefined;

        // Fetch all data in parallel
        // Fetch all agents (with high limit) for accurate totals, and paginated agents for table
        const [allAgentsData, agentsData, totalsData, companyData] = await Promise.all([
          fetchAgentsStats({
            startDate: startDateStr,
            endDate: endDateStr,
            skip: 0,
            limit: 1000, // High limit to get all agents for accurate totals
            search: search || undefined,
          }),
          fetchAgentsStats({
            startDate: startDateStr,
            endDate: endDateStr,
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            search: search || undefined,
          }),
          fetchTotalsStats({
            startDate: startDateStr,
            endDate: endDateStr,
          }),
          fetchCompanyStats({
            startDate: startDateStr,
            endDate: endDateStr,
          }),
        ]);

        setAllAgentsStats(allAgentsData);
        setAgentsStats(agentsData);
        setTotalsStats(totalsData);
        setCompanyStats(companyData);
        
        // Log API responses for debugging
        console.log('✅ API Calls Successful:', {
          agentsStats: {
            totalAgents: agentsData?.totalAgents,
            agentsCount: agentsData?.agents?.length,
          },
          totalsStats: {
            totalAgents: totalsData?.totalAgents,
            agentAds: totalsData?.agentAds,
          },
        });
      } catch (error) {
        console.error('❌ Error fetching company stats:', error);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, page, rowsPerPage, search]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload image
      const uploadType = 'userprofile';
      const uploadResult = await UploadAssetsApiCall(
        file,
        organizationId || '',
        accessToken,
        uploadType
      );

      if (uploadResult?.id) {
        // Update user profile with new attachment ID
        try {
          const response = await dispatch(updateUserProfile({
            id: id,
            name: name,
            language: userProfile.language,
            description: (userProfile as any).description || null,
            attachmentId: uploadResult.id,
          })).unwrap();
          
          dispatch(setUserProfile({ user: response }));
          toast.success('Profile picture updated successfully');
        } catch (error: any) {
          console.error('Error updating profile:', error);
          toast.error('Failed to update profile picture');
          setProfileImagePreview(
            userProfile?.userProfileImage?.key 
              ? getAssetURl(userProfile.userProfileImage.key)
              : null
          );
        }
      } else {
        toast.error('Failed to upload profile image');
        setProfileImagePreview(
          userProfile?.userProfileImage?.key 
            ? getAssetURl(userProfile.userProfileImage.key)
            : null
        );
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload profile image');
      setProfileImagePreview(
        userProfile?.userProfileImage?.key 
          ? getAssetURl(userProfile.userProfileImage.key)
          : null
      );
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Calculate agent statistics from all agents (not just paginated ones)
  const agentStatistics = allAgentsStats?.agents
    ? {
        adsCreated: totalsStats?.agentAds || 0,
        totalViews: allAgentsStats.agents.reduce((sum, agent) => sum + agent.totalViews, 0),
        totalContactClicks: allAgentsStats.agents.reduce((sum, agent) => sum + agent.totalContactClicks, 0),
      }
    : { adsCreated: totalsStats?.agentAds || 0, totalViews: 0, totalContactClicks: 0 };

  if (loading && !agentsStats) {
    return (
      <Page title="Company Dashboard">
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <Loader />
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Company Dashboard">
      <Container maxWidth="xl">
        <Grid container spacing={2} sx={{ pt: 3 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <Tooltip title="Click to change profile picture">
                  <IconButton
                    onClick={handleImageClick}
                    disabled={uploading}
                    sx={{
                      p: 0,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    <Avatar
                      src={profileImagePreview || undefined}
                      alt={name}
                      sx={{
                        width: 64,
                        height: 64,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        cursor: 'pointer',
                      }}
                    >
                      {!profileImagePreview && name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                {uploading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '50%',
                    }}
                  >
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  </Box>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </Box>
              <Box>
                <Typography variant="h4">
                  Hi, {name}. Welcome to Company Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Click on your profile picture to update it
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {error && (
          <Box sx={{ p: 2, mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {/* KPI Summary Cards */}
        <Grid container spacing={4} sx={{ mb: 3 }}>
          {/* Company Statistics (Company Only) */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Company Statistics
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Company Ads
                    </Typography>
                    <Typography variant="h5">
                      {companyStats?.adsCreated || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Company Views
                    </Typography>
                    <Typography variant="h5">
                      {companyStats?.totalViews?.toLocaleString() || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contact Interactions
                    </Typography>
                    <Typography variant="h5">
                      {companyStats?.totalContactClicks || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Agent Statistics (Total) */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Agents Statistics (Total)
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Agents
                    </Typography>
                    <Typography variant="h5">
                      {totalsStats?.totalAgents || agentsStats?.totalAgents || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Agent Ads
                    </Typography>
                    <Typography variant="h5">
                      {totalsStats?.agentAds || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Views
                    </Typography>
                    <Typography variant="h5">
                      {agentStatistics.totalViews?.toLocaleString() || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contact Interactions
                    </Typography>
                    <Typography variant="h5">
                      {agentStatistics.totalContactClicks || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Agent Details Table */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Agent Performance</Typography>
              <TextField
                placeholder="Search agents..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 1000 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Agent Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell align="right">Ads Created</TableCell>
                        <TableCell align="right">Total Views</TableCell>
                        <TableCell align="right">Phone</TableCell>
                        <TableCell align="right">WhatsApp</TableCell>
                        <TableCell align="right">Email</TableCell>
                        <TableCell align="right">Total Contacts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agentsStats?.agents && agentsStats.agents.length > 0 ? (
                        agentsStats.agents.map((agent) => (
                          <TableRow key={agent.agentId} hover>
                            <TableCell>{agent.agentName}</TableCell>
                            <TableCell>{agent.email}</TableCell>
                            <TableCell>{agent.phone}</TableCell>
                            <TableCell align="right">{agent.adsCreated}</TableCell>
                            <TableCell align="right">{agent.totalViews.toLocaleString()}</TableCell>
                            <TableCell align="right">{agent.phoneClicks}</TableCell>
                            <TableCell align="right">{agent.whatsappClicks}</TableCell>
                            <TableCell align="right">{agent.emailClicks}</TableCell>
                            <TableCell align="right">{agent.totalContactClicks}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No agents found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={agentsStats?.totalAgents || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default CompanyDashboard;
